import { useNavigate } from "#src/Routers/hooks";
import { useCreateInvestigation } from "#src/components/hooks/adapters/useInvestigations";
import { InvestigationsRoutePath } from "#src/routes/investigations";
import { InvestigationDetailRoutePath } from "#src/routes/investigations/[investigationId]";
import { NewInvestigationRoutePath } from "#src/routes/investigations/new";
import { useBreadcrumbsFromRoute } from "#src/utils/route";
import {
  Button,
  Form,
  Page,
  Panel,
  TextAreaInput,
  useForm,
} from "@validereinc/common-components";
import { MAX_INVESTIGATION_MESSAGE_LENGTH } from "@validereinc/domain";
import classNames from "classnames/bind";
import React from "react";
import styles from "./NewInvestigationPage.module.scss";

const cx = classNames.bind(styles);

type NewInvestigationFormType = {
  content: string;
};

export const NewInvestigationPage = () => {
  const navigate = useNavigate();
  const [breadcrumbs] = useBreadcrumbsFromRoute(NewInvestigationRoutePath);
  const form = useForm<NewInvestigationFormType>({
    mode: "onChange",
    defaultValues: { content: "" },
  });

  const createInvestigation = useCreateInvestigation();

  /** `Form` wires `react-hook-form` `handleSubmit`; pass only the submit handler body. */
  const onSubmitForm = async ({ content }: NewInvestigationFormType) => {
    const response = await createInvestigation.mutateAsync({ content });

    navigate({
      pathname: InvestigationDetailRoutePath.toLink({
        pathParams: { investigationId: response.data.investigation_id },
      }),
    });
  };

  return (
    <Page
      title={NewInvestigationRoutePath.title}
      breadcrumbs={breadcrumbs}
    >
      <Panel title="What should we investigate?">
        <Form
          {...form}
          onSubmit={onSubmitForm}
        >
          <TextAreaInput
            name="content"
            label="Prompt"
            placeholder="Describe the issue or question you want the agent to investigate..."
            isRequired
            isFluid
            rules={{
              validate: {
                maxLength: (value: string) =>
                  value.length <= MAX_INVESTIGATION_MESSAGE_LENGTH ||
                  `Prompt must be ${MAX_INVESTIGATION_MESSAGE_LENGTH.toLocaleString()} characters or fewer.`,
              },
            }}
          />
          <div className={cx("formActions")}>
            <Button
              type="button"
              variant="outline"
              size="small"
              onClick={() =>
                navigate({ pathname: InvestigationsRoutePath.toLink() })
              }
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="small"
              isLoading={createInvestigation.isLoading}
              disabled={
                !form.formState.isValid || createInvestigation.isLoading
              }
            >
              Start Investigation
            </Button>
          </div>
        </Form>
      </Panel>
    </Page>
  );
};
